'use client'

import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  SearchIcon,
  FileText,
  Bot,
  Settings2,
  Users,
  Brain,
  ArrowUpRightIcon,
  PlusIcon,
  FileInputIcon,
  FolderPlusIcon,
  CircleFadingPlusIcon,
  Home,
  History,
  Star,
  MessageSquare,
  Key,
  CreditCard,
  Activity,
  FileCode,
  Edit,
  UserPlus,
} from 'lucide-react'

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'

interface SearchItem {
  id: string
  title: string
  description?: string
  icon: React.ElementType
  action: () => void
  shortcut?: string
  keywords?: string[]
}

// Create search items factory functions to avoid hook dependency issues
const createNavigationItems = (
  navigate: ReturnType<typeof useNavigate>
): SearchItem[] => [
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Go to main dashboard',
    icon: Home,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['home', 'main', 'overview'],
  },
  {
    id: 'chat',
    title: 'Chat',
    description: 'Start a new AI conversation',
    icon: MessageSquare,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['ai', 'conversation', 'talk', 'message'],
  },
  {
    id: 'history',
    title: 'History',
    description: 'View chat history',
    icon: History,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['past', 'previous', 'logs'],
  },
  {
    id: 'favorites',
    title: 'Favorites',
    description: 'View favorite chats',
    icon: Star,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['starred', 'saved', 'bookmarks'],
  },
  {
    id: 'models-gpt4',
    title: 'GPT-4',
    description: 'Configure GPT-4 model',
    icon: Bot,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['openai', 'chatgpt', 'ai model'],
  },
  {
    id: 'models-claude',
    title: 'Claude',
    description: 'Configure Claude model',
    icon: Bot,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['anthropic', 'ai model'],
  },
  {
    id: 'models-gemini',
    title: 'Gemini',
    description: 'Configure Gemini model',
    icon: Bot,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['google', 'ai model'],
  },
  {
    id: 'knowledge-docs',
    title: 'Documents',
    description: 'Manage knowledge base documents',
    icon: FileText,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['files', 'papers', 'pdfs'],
  },
  {
    id: 'knowledge-sources',
    title: 'Sources',
    description: 'Manage data sources',
    icon: FileCode,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['data', 'references', 'links'],
  },
  {
    id: 'knowledge-training',
    title: 'Training',
    description: 'Train on your data',
    icon: Brain,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['learn', 'teach', 'customize'],
  },
  {
    id: 'settings-general',
    title: 'General Settings',
    description: 'Configure general settings',
    icon: Settings2,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['preferences', 'config', 'options'],
  },
  {
    id: 'settings-api',
    title: 'API Keys',
    description: 'Manage API keys',
    icon: Key,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['tokens', 'credentials', 'auth'],
  },
  {
    id: 'settings-billing',
    title: 'Billing',
    description: 'Manage billing and subscription',
    icon: CreditCard,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['payment', 'subscription', 'plan'],
  },
  {
    id: 'settings-usage',
    title: 'Usage',
    description: 'View usage statistics',
    icon: Activity,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['stats', 'analytics', 'metrics'],
  },
  {
    id: 'project-research',
    title: 'Research Assistant',
    description: 'Open research project',
    icon: FileText,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['study', 'investigate', 'analyze'],
  },
  {
    id: 'project-content',
    title: 'Content Creation',
    description: 'Open content project',
    icon: Edit,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['write', 'create', 'generate'],
  },
  {
    id: 'project-team',
    title: 'Team Collaboration',
    description: 'Open team project',
    icon: Users,
    action: () => navigate({ to: '/dashboard' }),
    keywords: ['collaborate', 'share', 'work together'],
  },
]

const createCommandItems = (
  navigate: ReturnType<typeof useNavigate>
): SearchItem[] => [
  {
    id: 'cmd-new-chat',
    title: 'New Chat',
    description: 'Start a new AI conversation',
    icon: PlusIcon,
    action: () => {
      navigate({ to: '/dashboard' })
      // Trigger new chat action
    },
    shortcut: '⌘N',
    keywords: ['create', 'start', 'begin'],
  },
  {
    id: 'cmd-import-doc',
    title: 'Import Document',
    description: 'Import a document to knowledge base',
    icon: FileInputIcon,
    action: () => {
      navigate({ to: '/dashboard' })
      // Trigger import dialog
    },
    shortcut: '⌘I',
    keywords: ['upload', 'add', 'file'],
  },
  {
    id: 'cmd-new-folder',
    title: 'New Folder',
    description: 'Create a new folder',
    icon: FolderPlusIcon,
    action: () => {
      // Trigger new folder creation
    },
    shortcut: '⌘⇧N',
    keywords: ['directory', 'organize'],
  },
  {
    id: 'cmd-add-model',
    title: 'Add AI Model',
    description: 'Configure a new AI model',
    icon: CircleFadingPlusIcon,
    action: () => {
      navigate({ to: '/dashboard' })
    },
    shortcut: '⌘M',
    keywords: ['configure', 'setup', 'model'],
  },
  {
    id: 'cmd-invite-user',
    title: 'Invite Team Member',
    description: 'Invite someone to collaborate',
    icon: UserPlus,
    action: () => {
      navigate({ to: '/dashboard' })
      // Trigger invite dialog
    },
    shortcut: '⌘⇧I',
    keywords: ['share', 'collaborate', 'add user'],
  },
]

export function UniversalSearch() {
  const [open, setOpen] = React.useState(false)
  const navigate = useNavigate()

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'p' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(open => !open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const [searchQuery, setSearchQuery] = React.useState('')

  const navigationItems = React.useMemo(
    () => createNavigationItems(navigate),
    [navigate]
  )
  const commandItems = React.useMemo(
    () => createCommandItems(navigate),
    [navigate]
  )

  const filteredItems = React.useMemo(() => {
    if (!searchQuery)
      return { navigation: navigationItems, commands: commandItems }

    const query = searchQuery.toLowerCase()

    // Check if it's a command (starts with /)
    if (query.startsWith('/')) {
      const cmdQuery = query.slice(1)
      return {
        navigation: [],
        commands: commandItems.filter(
          (item: SearchItem) =>
            item.title.toLowerCase().includes(cmdQuery) ||
            item.description?.toLowerCase().includes(cmdQuery) ||
            item.keywords?.some((k: string) =>
              k.toLowerCase().includes(cmdQuery)
            )
        ),
      }
    }

    // Regular search
    const filterItems = (items: SearchItem[]) =>
      items.filter(
        (item: SearchItem) =>
          item.title.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.keywords?.some((k: string) => k.toLowerCase().includes(query))
      )

    return {
      navigation: filterItems(navigationItems),
      commands: filterItems(commandItems),
    }
  }, [searchQuery, navigationItems, commandItems])

  const handleSelect = (item: SearchItem) => {
    setOpen(false)
    item.action()
  }

  return (
    <>
      <button
        className="w-full border-input bg-zinc-50 dark:bg-zinc-900 text-foreground placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:ring-ring/50 inline-flex h-9 rounded-md border px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px]"
        onClick={() => setOpen(true)}
      >
        <span className="flex grow items-center">
          <SearchIcon
            className="text-muted-foreground/80 -ms-1 me-3"
            size={16}
            aria-hidden="true"
          />
          <span className="text-muted-foreground/70 font-normal">Search</span>
        </span>
        <kbd className="bg-background text-muted-foreground/70 ms-12 -me-1 inline-flex h-5 max-h-full items-center rounded border px-1 font-[inherit] text-[0.625rem] font-medium">
          ⌘ + P
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search or type / for commands..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          <CommandEmpty>
            No results found. Try typing / for commands.
          </CommandEmpty>

          {filteredItems.commands.length > 0 && (
            <>
              <CommandGroup heading="Commands">
                {filteredItems.commands.map(item => (
                  <CommandItem
                    key={item.id}
                    onSelect={() => handleSelect(item)}
                  >
                    <item.icon
                      size={16}
                      className="opacity-60 mr-2"
                      aria-hidden="true"
                    />
                    <div className="flex-1">
                      <div>{item.title}</div>
                      {item.description && (
                        <div className="text-xs text-muted-foreground">
                          {item.description}
                        </div>
                      )}
                    </div>
                    {item.shortcut && (
                      <CommandShortcut className="justify-center">
                        {item.shortcut}
                      </CommandShortcut>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
              {filteredItems.navigation.length > 0 && <CommandSeparator />}
            </>
          )}

          {filteredItems.navigation.length > 0 && (
            <CommandGroup heading="Navigation">
              {filteredItems.navigation.map(item => (
                <CommandItem key={item.id} onSelect={() => handleSelect(item)}>
                  <item.icon
                    size={16}
                    className="opacity-60 mr-2"
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <div>{item.title}</div>
                    {item.description && (
                      <div className="text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    )}
                  </div>
                  <ArrowUpRightIcon
                    size={14}
                    className="opacity-40"
                    aria-hidden="true"
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  )
}
